import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../App';
import { BACKEND_URL } from '../../config/api';
import Sidebar from './Sidebar';
import NotificationCenter from '../NotificationCenter';
import { Sheet, SheetContent, SheetTitle } from '../ui/sheet';
import { Menu, User, HelpCircle, LogOut, ChevronDown, DollarSign, Eye, EyeOff } from 'lucide-react';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '../ui/alert-dialog';
import '../../styles/sidebar.css';

const SIDEBAR_KEY = 'taskeri_sidebar_collapsed';

// Routes that should NOT show the sidebar layout
const NON_SIDEBAR_ROUTES = [
  '/',
  '/ti-admin',
];

// Routes where sidebar should auto-collapse (content-heavy pages)
const AUTO_COLLAPSE_ROUTES = ['/browse-jobs', '/messages'];

const DashboardLayout = ({ children }) => {
  const { user, setUser, logout } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false); // Default: expanded
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [showEarnings, setShowEarnings] = useState(false);
  const accountMenuRef = React.useRef(null);

  const isHeroUser = user?.role_preference === 'hero' || user?.role_preference === 'both';

  // Total earnings for the account dropdown — refreshed each time the menu opens
  useEffect(() => {
    if (!isHeroUser || !showAccountMenu) return;
    axios.get(`${BACKEND_URL}/api/payments/total-earnings`, { withCredentials: true })
      .then(res => setTotalEarnings(res.data.total_earnings || 0))
      .catch(err => console.debug('[DashboardLayout] earnings fetch failed:', err?.response?.status));
  }, [isHeroUser, showAccountMenu]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile sidebar on navigation
  useEffect(() => {
    setIsMobileOpen(false);
    setShowAccountMenu(false);
  }, [location.pathname]);

  // Auto-collapse sidebar on content-heavy pages (only on initial navigation, not on manual toggle)
  // NOTE: Does NOT persist to localStorage — only affects current navigation
  const lastAutoCollapsedRoute = React.useRef(null);
  useEffect(() => {
    if (isMobile) return;
    const shouldCollapse = AUTO_COLLAPSE_ROUTES.some(r => location.pathname.startsWith(r));
    if (shouldCollapse && !isCollapsed && lastAutoCollapsedRoute.current !== location.pathname) {
      setIsCollapsed(true);
      // Don't persist auto-collapse — user can expand manually
    }
    lastAutoCollapsedRoute.current = shouldCollapse ? location.pathname : null;
  }, [location.pathname, isMobile]);

  // Close account menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target)) {
        setShowAccountMenu(false);
      }
    };
    if (showAccountMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showAccountMenu]);

  const toggleSidebar = () => {
    setIsCollapsed(prev => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_KEY, String(next));
      return next;
    });
  };

  const handleLogout = async () => {
    setShowLogoutDialog(false);
    setIsMobileOpen(false);
    await logout();
  };

  // Determine if sidebar should show
  const shouldShowSidebar = (() => {
    if (!user) return false;
    const path = location.pathname;
    if (path.startsWith('/ti-admin')) return false;
    if (NON_SIDEBAR_ROUTES.some(route => path === route)) return false;
    return true;
  })();

  // Set body class for sidebar state (so external components can adjust)
  useEffect(() => {
    if (shouldShowSidebar) {
      document.body.classList.add('has-sidebar');
      document.body.classList.toggle('sidebar-is-collapsed', isCollapsed && !isMobile);
      document.body.classList.toggle('sidebar-is-expanded', !isCollapsed && !isMobile);
    } else {
      document.body.classList.remove('has-sidebar', 'sidebar-is-collapsed', 'sidebar-is-expanded');
    }
    return () => {
      document.body.classList.remove('has-sidebar', 'sidebar-is-collapsed', 'sidebar-is-expanded');
    };
  }, [shouldShowSidebar, isCollapsed, isMobile]);

  if (!shouldShowSidebar) {
    // Authenticated users on non-sidebar routes get a minimal topbar with logo
    // Unauthenticated users already get TopNav from App.js
    if (user) {
      return (
        <>
          <div className="public-topbar" data-testid="public-topbar">
            <a href="/dashboard" className="public-topbar-logo">
              <picture>
                <source srcSet="/logo-dark.webp" type="image/webp" />
                <img src="/logo-dark.png" alt="taskeri®" style={{ height: '28px', width: 'auto' }} />
              </picture>
            </a>
          </div>
          {children}
        </>
      );
    }
    return <>{children}</>;
  }

  return (
    <div className="dashboard-layout" data-testid="dashboard-layout">
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      )}

      {/* Mobile Sidebar (Sheet/Drawer) */}
      {isMobile && (
        <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
          <SheetContent side="left" className="sidebar-sheet-content">
            <VisuallyHidden.Root>
              <SheetTitle>Navigation Menu</SheetTitle>
            </VisuallyHidden.Root>
            <Sidebar isCollapsed={false} onToggle={() => setIsMobileOpen(false)} onLogoutRequest={() => { setIsMobileOpen(false); setShowLogoutDialog(true); }} />
          </SheetContent>
        </Sheet>
      )}

      {/* Main Content */}
      <div className={`dashboard-main ${!isMobile ? (isCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded') : 'no-sidebar'}`}>
        {/* Slim Top Bar */}
        <header className="slim-topbar" data-testid="slim-topbar">
          {isMobile && (
            <button
              className="mobile-menu-trigger"
              onClick={() => setIsMobileOpen(true)}
              data-testid="mobile-menu-trigger"
            >
              <Menu size={22} />
            </button>
          )}
          <div className="topbar-logo-mobile" onClick={() => navigate('/dashboard')}>
            {isMobile && (
              <picture>
                <source srcSet="/logo-dark.webp" type="image/webp" />
                <img src="/logo-dark.png" alt="taskeri®" style={{ height: '26px', width: 'auto', cursor: 'pointer' }} />
              </picture>
            )}
          </div>

          <div className="topbar-spacer" />

          {/* Right side: Notifications + Account Menu */}
          <div className="topbar-right">
            <NotificationCenter />
            <div className="topbar-account-menu" ref={accountMenuRef}>
              <button 
                className="topbar-user-pill" 
                onClick={() => setShowAccountMenu(prev => !prev)} 
                data-testid="topbar-account-btn"
              >
                {user?.picture ? (
                  <img src={user.picture} alt="" className="topbar-user-img" />
                ) : (
                  <div className="topbar-user-initials">{(user?.name || 'U')[0].toUpperCase()}</div>
                )}
                {!isMobile && <span className="topbar-user-name">{user?.name?.split(' ')[0]}</span>}
                <ChevronDown size={14} style={{ opacity: 0.5, transition: 'transform 0.2s', transform: showAccountMenu ? 'rotate(180deg)' : 'rotate(0)' }} />
              </button>
              {showAccountMenu && (
                <div className="topbar-dropdown" data-testid="topbar-dropdown">
                  {isHeroUser && (
                    <>
                      <div className="topbar-dropdown-earnings" data-testid="dropdown-earnings">
                        <button
                          className="topbar-earnings-value"
                          onClick={() => { setShowAccountMenu(false); navigate('/my-earnings'); }}
                          title="View earnings details"
                          data-testid="earnings-badge"
                        >
                          <DollarSign size={15} />
                          <span className="topbar-earnings-label">Earnings</span>
                          <span className="topbar-earnings-amount">
                            {showEarnings ? `$${Number(totalEarnings).toFixed(2)}` : '••••••'}
                          </span>
                        </button>
                        <button
                          className="topbar-earnings-toggle"
                          onClick={() => setShowEarnings(prev => !prev)}
                          title={showEarnings ? 'Hide earnings' : 'Show earnings'}
                          data-testid="toggle-earnings-btn"
                        >
                          {showEarnings ? <EyeOff size={15} /> : <Eye size={15} />}
                        </button>
                      </div>
                      <div className="topbar-dropdown-divider" />
                    </>
                  )}
                  <button className="topbar-dropdown-item" onClick={() => navigate('/profile')} data-testid="topbar-profile">
                    <User size={16} /> Profile
                  </button>
                  <button className="topbar-dropdown-item" onClick={() => navigate('/help')} data-testid="topbar-help">
                    <HelpCircle size={16} /> Help Center
                  </button>
                  <div className="topbar-dropdown-divider" />
                  <button className="topbar-dropdown-item topbar-dropdown-logout" onClick={() => {
                    setShowAccountMenu(false);
                    setShowLogoutDialog(true);
                  }} data-testid="topbar-logout">
                    <LogOut size={16} /> Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="dashboard-content">
          {children}
        </main>

        {/* Slim Footer */}
        <footer className="dashboard-footer" data-testid="dashboard-footer">
          <span>&copy; 2026 taskeri® Technologies Inc. All rights reserved. taskeri<sup style={{fontSize:'0.6em'}}>®</sup> is a registered trademark.</span>
          <span className="dashboard-footer-links">
            <a href="/privacy">Privacy</a>
            <a href="/cookie-policy">Cookies</a>
            <a href="/accessibility">Accessibility</a>
          </span>
        </footer>
      </div>

      {/* Logout Dialog - rendered at layout level so it works on both mobile and desktop */}
      <AlertDialog open={showLogoutDialog} onOpenChange={(open) => { if (!open) setShowLogoutDialog(false); }}>
        <AlertDialogContent className="logout-dialog" data-testid="layout-logout-dialog" style={{ zIndex: 50001 }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontSize: '18px', fontWeight: 600, color: '#111827', WebkitTextFillColor: '#111827', background: 'none' }}>
              Sign out of taskeri<sup style={{fontSize:'0.55em'}}>®</sup>?
            </AlertDialogTitle>
            <AlertDialogDescription style={{ fontSize: '14px', color: '#6b7280', lineHeight: 1.5 }}>
              You'll need to sign in again to access your dashboard, messages, and ongoing jobs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', gap: '8px' }}>
            <AlertDialogCancel
              data-testid="layout-logout-cancel"
              style={{ borderRadius: '10px', fontWeight: 500, margin: 0 }}
            >
              Stay signed in
            </AlertDialogCancel>
            <button
              onClick={handleLogout}
              onTouchEnd={(e) => { e.preventDefault(); handleLogout(); }}
              data-testid="layout-logout-confirm"
              style={{
                borderRadius: '10px',
                fontWeight: 500,
                background: '#dc2626',
                color: '#fff',
                margin: 0,
                padding: '8px 16px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Sign out
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DashboardLayout;
